import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtNZD, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";
import { errMsg, nzDateTime } from "@/lib/hsUtil";

const num = v => (v === "" || v === null || v === undefined ? null : parseFloat(v));

export const EstimateFields = ({ v, set, prefix = "est" }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${prefix}-fields`}>
    <label className="text-sm block">Estimated value (ex GST)
      <NumberInput className="input w-full mt-1" value={v.value} onChange={e => set("value", e.target.value)} data-testid={`${prefix}-value`}/></label>
    <label className="text-sm block">Estimate date
      <DateInput className="input w-full mt-1" value={v.date || ""} onChange={e => set("date", e.target.value)} data-testid={`${prefix}-date`}/></label>
    <label className="text-sm block">Range low (optional)
      <NumberInput className="input w-full mt-1" value={v.low ?? ""} onChange={e => set("low", e.target.value)} data-testid={`${prefix}-low`}/></label>
    <label className="text-sm block">Range high (optional)
      <NumberInput className="input w-full mt-1" value={v.high ?? ""} onChange={e => set("high", e.target.value)} data-testid={`${prefix}-high`}/></label>
    <label className="text-sm block sm:col-span-2">Estimate notes
      <textarea className="input w-full mt-1" rows={3} value={v.notes || ""} onChange={e => set("notes", e.target.value)} data-testid={`${prefix}-notes`}/></label>
  </div>
);

export const estBody = v => ({ value: num(v.value) || 0, low: num(v.low), high: num(v.high), notes: v.notes || "", date: v.date || null });

const FLAG = { green: "border-green-600 text-green-400", amber: "border-amber-400 text-amber-300", red: "border-red-500 text-red-400", none: "border-neutral-700 opacity-80" };

const Stat = ({ label, value, testid }) => (
  <div><div className="text-xs opacity-60">{label}</div><div className="text-silver" data-testid={testid}>{value}</div></div>
);

const History = ({ rows }) => rows.length === 0 ? null : (
  <details className="text-sm" data-testid="est-history">
    <summary className="cursor-pointer opacity-80">Estimate history ({rows.length})</summary>
    <ul className="mt-2 space-y-1">
      {[...rows].reverse().map((h, i) => (
        <li key={i} className="text-xs" data-testid={`est-history-${i}`}>
          <span className="opacity-60">{nzDateTime(h.at)}{h.by ? ` · ${h.by}` : ""}</span> · {h.what}
          {(h.old || h.new) && <span className="opacity-80"> {h.old ? `was ${h.old}` : ""}{h.new ? ` → now ${h.new}` : ""}</span>}
        </li>))}
    </ul>
  </details>
);

export default function EstimatePanel({ project, reload }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "Admin";
  const [s, setS] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/projects/${project.id}/estimate-pricing`).then(r => {
    setS(r.data);
    if (isAdmin && !r.data.estimate.value) setEdit(e => e || { ...r.data.estimate });
  }).catch(e => toast.error(errMsg(e, "Could not load the estimate"))), [project.id, isAdmin]);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const save = async () => {
    try { const r = await api.put(`/projects/${project.id}/estimate-pricing`, estBody(edit)); setS(r.data); setEdit(null); toast.success("Estimate saved"); reload?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async to => {
    const msg = to === "fixed" ? "Convert to Fixed? You'll build and accept a quote. The estimate is kept as a starting figure only. Bills, hours and reports are not changed."
      : "Convert to Charge-up? All hours, bills and reports are kept. The estimate history is kept.";
    if (!window.confirm(msg)) return;
    try { await api.post(`/projects/${project.id}/pricing`, { to }); toast.success("Converted");
      if (to === "fixed") { nav(`/projects/${project.id}/estimate`); } reload?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const c = s.costs;
  return (
    <div className={`card border-l-4 space-y-3 ${FLAG[s.flag]}`} data-testid="estimate-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-silver mr-auto" data-testid="est-basis">Actual cost vs estimate</h3>
        {isAdmin && !edit && <button className="btn-ghost text-sm" onClick={() => setEdit({ ...s.estimate })} data-testid="est-edit-btn">Edit estimate</button>}
        {isAdmin && <button className="btn-ghost text-sm" onClick={() => convert("chargeup")} data-testid="est-to-chargeup">Convert to Charge-up</button>}
        {isAdmin && <button className="btn-ghost text-sm" onClick={() => convert("fixed")} data-testid="est-to-fixed">Convert to Fixed</button>}
      </div>
      {s.flag === "amber" && <div className="text-amber-300 text-sm" data-testid="est-flag-amber">Heads up: actual cost has passed {s.ranged ? "80% of the low end" : "80% of the estimate"} ({fmtNZD(s.amber_at)}).</div>}
      {s.flag === "red" && <div className="text-red-400 text-sm font-semibold" data-testid="est-flag-red">Over: actual cost has passed the {s.compare_label} ({fmtNZD(s.compare_to)}).</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Estimated value (ex GST)" value={s.label} testid="est-value-label"/>
        <Stat label="Actual cost to date" value={fmtNZD(s.actual_cost)} testid="est-actual-cost"/>
        <Stat label={`% of ${s.compare_label} used (actual cost)`} value={s.pct_used == null ? "—" : `${s.pct_used}%`} testid="est-pct"/>
        <Stat label="Remaining" value={s.remaining == null ? "—" : s.remaining < 0 ? `${fmtNZD(-s.remaining)} over` : fmtNZD(s.remaining)} testid="est-remaining"/>
        <Stat label="Materials (at cost)" value={fmtNZD(c.materials)} testid="est-cost-materials"/>
        <Stat label={`Labour (at cost, ${c.hours} h)`} value={fmtNZD(c.labour)} testid="est-cost-labour"/>
        <Stat label="Subcontractors (at cost)" value={fmtNZD(c.subcontractors)} testid="est-cost-subs"/>
        <Stat label="Charged so far (billing)" value={fmtNZD(s.chargeup_total)} testid="est-chargeup-total"/>
      </div>
      {!edit && (s.estimate.notes || s.estimate.date) && <div className="text-sm opacity-80 whitespace-pre-wrap" data-testid="est-notes-view">
        {s.estimate.date && <span className="opacity-60">Estimate date {fmtDate(s.estimate.date)}. </span>}{s.estimate.notes}</div>}
      {edit && (<div className="space-y-2">
        <EstimateFields v={edit} set={(k, v) => setEdit(x => ({ ...x, [k]: v }))} prefix="est-edit"/>
        <div className="flex gap-2"><button className="btn-silver text-sm" onClick={save} data-testid="est-save">Save estimate</button>
          <button className="btn-ghost text-sm" onClick={() => setEdit(null)} data-testid="est-cancel">Cancel</button></div>
      </div>)}
      <div className="text-xs opacity-60">An estimate is not a contract. Hours, bills and manual lines on the Charge-up tab build the actual figures. Flags, % used and remaining compare actual cost (materials, labour and subcontractors at cost) with the estimate{s.ranged ? ": amber past 80% of the low end, red above the high end" : ": amber past 80%, red above 100%"}. "Charged so far" is what the charge-up would bill.</div>
      <History rows={s.history}/>
    </div>
  );
}
